import { Canvas, useFrame } from "@react-three/fiber";
import { MeshDistortMaterial, Float } from "@react-three/drei";
import { Suspense, useRef } from "react";
import * as THREE from "three";

function MorphSphere({ color = "#ff4500", emissive = "#ff2a00" }) {
  const ref = useRef(null);
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.getElapsedTime();
    ref.current.rotation.y = t * 0.18;
    ref.current.rotation.x = Math.sin(t * 0.2) * 0.2;
    ref.current.position.y = Math.sin(t * 0.5) * 0.18;
  });
  return (
    <group ref={ref}>
      <mesh>
        <icosahedronGeometry args={[1.55, 64]} />
        <MeshDistortMaterial
          color={color}
          emissive={emissive}
          emissiveIntensity={0.35}
          roughness={0.25}
          metalness={0.6}
          distort={0.45}
          speed={1.6}
        />
      </mesh>
      <mesh scale={1.65}>
        <icosahedronGeometry args={[1.55, 1]} />
        <meshBasicMaterial color={color} wireframe transparent opacity={0.18} />
      </mesh>
    </group>
  );
}

function FloatingShard({ position, color, scale = 0.4, geo = "tetra" }) {
  const r = useRef(null);
  useFrame((s) => {
    if (!r.current) return;
    const t = s.clock.getElapsedTime();
    r.current.rotation.x = t * 0.3;
    r.current.rotation.y = t * 0.45;
  });
  return (
    <Float speed={1.4} rotationIntensity={0.6} floatIntensity={1.2}>
      <mesh position={position} ref={r} scale={scale}>
        {geo === "tetra" && <tetrahedronGeometry args={[1, 0]} />}
        {geo === "oct" && <octahedronGeometry args={[1, 0]} />}
        {geo === "torus" && <torusGeometry args={[0.7, 0.18, 12, 36]} />}
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.5}
          wireframe
        />
      </mesh>
    </Float>
  );
}

function FuturisticGrid({ cellColor = "#1a1a1d", sectionColor = "#ff4500" }) {
  const ref = useRef(null);
  useFrame((s) => {
    if (!ref.current) return;
    const t = s.clock.getElapsedTime();
    ref.current.position.z = (t * 0.6) % 3;
  });
  return (
    <group position={[0, -2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <gridHelper
        ref={ref}
        args={[60, 60, sectionColor, cellColor]}
      />
    </group>
  );
}

function MouseParallax() {
  useFrame((state) => {
    const { camera, mouse } = state;
    camera.position.x += (mouse.x * 0.8 - camera.position.x) * 0.04;
    camera.position.y += (-mouse.y * 0.4 + 0.2 - camera.position.y) * 0.04;
    camera.lookAt(0, 0, 0);
  });
  return null;
}

export default function HeroScene({ theme = "dark" }) {
  const isLight = theme === "light";
  const bg = isLight ? "#f4efe6" : "#050505";
  const fogColor = isLight ? "#f4efe6" : "#050505";
  const gridCell = isLight ? "#d6cdb8" : "#1a1a1d";
  const gridSection = isLight ? "#cc3300" : "#ff4500";
  const sphereColor = isLight ? "#cc3300" : "#ff4500";
  const sphereEmissive = isLight ? "#a32600" : "#ff2a00";
  const limeColor = isLight ? "#5a7d00" : "#ccff00";
  return (
    <Canvas
      key={theme}
      dpr={[1, 1.6]}
      camera={{ position: [0, 0.2, 5], fov: 50 }}
      gl={{ antialias: true, alpha: true }}
      onCreated={({ scene }) => {
        scene.fog = new THREE.Fog(fogColor, 6, 22);
      }}
    >
      <color attach="background" args={[bg]} />
      <ambientLight intensity={isLight ? 0.6 : 0.25} />
      <pointLight position={[4, 3, 4]} intensity={2.5} color={sphereColor} />
      <pointLight position={[-4, -2, 3]} intensity={2.0} color={limeColor} />
      <pointLight position={[0, 5, -3]} intensity={1.2} color="#ffffff" />

      <Suspense fallback={null}>
        <MorphSphere color={sphereColor} emissive={sphereEmissive} />
        <FloatingShard position={[-2.6, 1.2, -1]} color={limeColor} scale={0.45} geo="tetra" />
        <FloatingShard position={[2.7, -0.6, -0.5]} color={sphereColor} scale={0.55} geo="oct" />
        <FloatingShard position={[1.4, 1.7, -2]} color={isLight ? "#14110c" : "#ffffff"} scale={0.35} geo="torus" />
        <FloatingShard position={[-1.8, -1.4, -1.5]} color={sphereColor} scale={0.4} geo="tetra" />
        <FloatingShard position={[0.2, -2.0, -3]} color={limeColor} scale={0.5} geo="oct" />
        <FuturisticGrid cellColor={gridCell} sectionColor={gridSection} />
      </Suspense>

      <MouseParallax />
    </Canvas>
  );
}
